import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import translations from '../../translation'; // Import translations

const ApprovedTable = ({ language }) => {
  const translate = (key) => translations[language]?.[key] || key;

  const [modalShow, setModalShow] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [approvedData, setApprovedData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const rowsPerPage = 10;

  useEffect(() => {
    fetchApprovedData();
  }, []);

  const fetchApprovedData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append('id', user.id);

      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/approval/approve`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setApprovedData(data.data || []);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error('Error fetching approved data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = approvedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(approvedData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setModalShow(true);
  };

  return (
    <div className="bg-white shadow rounded-lg mb-8 w-full">
      <h3 className="px-4 py-5 text-lg font-medium text-gray-900">
        {translate('approvedTableTitle')}
      </h3>
      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="px-4 py-6 text-center text-gray-500">
            {translate('loadingText')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {translate('titleLabel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {translate('submittedByLabel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {translate('dateLabel')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {translate('statusLabel')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {translate('actionsLabel')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.map((row, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm text-gray-900">{row.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{row.username}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(row.sent_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {translate('approved')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => handleView(row)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-custom hover:text-custom-dark border border-custom rounded-button"
                      >
                        {translate('viewButton')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="d-flex justify-between items-center px-6 py-3 space-x-2">
      <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
              currentPage === 1 ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {translate('prevButton')}
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
              currentPage === totalPages ? 'bg-gray-300 text-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {translate('nextButton')}
          </button>
      </div>
      <Modal
        size="xl"
        show={modalShow}
        onHide={() => setModalShow(false)}
        centered
        dialogClassName="modal-75w"
      >
        <Modal.Header closeButton>
          <Modal.Title>{translate('taskDetailsTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{translate('fieldLabel')}</th>
                <th>{translate('valueLabel')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{translate('taskTitleLabel')}</td>
                <td>{selectedTask?.title || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('taskContentLabel')}</td>
                <td>{selectedTask?.content || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('submittedByLabel')}</td>
                <td>{selectedTask?.username || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('approvalStatusLabel')}</td>
                <td>{translate('approved') || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('approvalDateLabel')}</td>
                <td>
                  {selectedTask?.approved_date
                    ? new Date(selectedTask?.approved_date).toLocaleDateString()
                    : translate('notApproved')}
                </td>
              </tr>
              <tr>
                <td>{translate('rejectionReasonLabel')}</td>
                <td>{selectedTask?.rejection_reason || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('methodLabel')}</td>
                <td>{selectedTask?.method || translate('notAvailable')}</td>
              </tr>
              <tr>
                <td>{translate('createdAtLabel')}</td>
                <td>
                  {selectedTask?.created_at
                    ? new Date(selectedTask?.created_at).toLocaleDateString()
                    : translate('notAvailable')}
                </td>
              </tr>
              <tr>
                <td>{translate('sentAtLabel')}</td>
                <td>
                  {selectedTask?.sent_at
                    ? new Date(selectedTask?.sent_at).toLocaleDateString()
                    : translate('notAvailable')}
                </td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ApprovedTable;
